<template>
  <div>
    <!-- Inline -->
    <div class="row q-mb-md">
      <!-- Filters hide/show button -->
      <div class="col-6">
        <div class="row" style="justify-content: flex-start">
          <div>
            <q-btn
              @click="showFilters();"
              :text-color="hideFilters ? 'grey-5' : 'white'"
              class="neutral-btn ctrl-button btn-compact"
              :class="{ primary: !hideFilters }"
              icon="filter_list"
              :icon-right="hideFilters ? 'keyboard_arrow_up' : 'keyboard_arrow_down'"
            />
          </div>
          <!-- Searchfield -->
          <div class="q-ml-md" style="flex-grow: 1">
            <q-input
              color="grey-5"
              v-model="searchTerm"
              @input="applyFilters"
              :placeholder="getString('module.redirect.list.filter.label')"
            />
          </div>
        </div>
      </div>
      <!-- Invite button -->
      <div class="col-6" style="justify-content: flex-end">
        <q-btn v-if="$store.state.meStore.me.rights.includes('redirect.add')"
          class="capitalize ctrl-button right"
          color="ctrl-turquoise"
          :label="getString('action.add')"
          @click="$router.push('/redirect/add')"
          icon-right="add"
        />
        <q-btn v-if="$store.state.meStore.me.rights.includes('redirect.delete')"
          class="capitalize ctrl-button bg-ctrl-grey right q-mr-md"
          color="ctrl-turquoise"
          :disabled="!rowSelection.length"
          :label="$store.state.stringsStore.strings.list.actions.label"
          :class="{ 'button-active': rowSelection.length }">
            <q-icon size="large" name="mdi-chevron-down"/>
            <i v-show="rowSelection.length" class="counter">{{rowSelection.length}}</i>
            <!-- Direct child of target -->
            <q-popover anchor="bottom middle" self="top middle">
              <q-list link>
                <q-item
                  :class="{ 'disabled': !rowSelection.length }"
                  class="toolbar-list-item" @click.native="removeRows('redirects')">
                  <q-icon color="positive" size="large" name="delete"/>
                  {{$store.state.stringsStore.strings.action.remove}}
                </q-item>
              </q-list>
            </q-popover>
        </q-btn>
      </div>
    </div>
    <!-- Filters block -->
    <transition name="fade">
    <div v-if="!hideFilters" class="table-block q-mb-md">
      <div class="row q-pa-md" style="justify-content: space-between;">
        <!-- Searchfield -->
        <div class="col-6 q-pa-md">
          <q-input color="primary"
            v-model="filters.location_from"
            @input="applyFilters"
          :float-label="getString('module.redirect.fields.location_from')"/>
        </div>
        <!-- Searchfield -->
        <div class="col-6 q-pa-md">
          <q-input color="primary"
          v-model="filters.location_to"
          @input="applyFilters"
          :float-label="getString('module.redirect.fields.location_to')"/>
        </div>
      </div>
      <!-- Row 2 -->
      <div class="row q-pa-md" style="justify-content: space-between;">
        <!-- Searchfield -->
        <div class="col-6 q-pa-md">
          <q-select
            class="full-width q-mb-md"
            v-model="filters.status"
            @input="applyFilters"
            :options="[{ label: '301', value: 301 }, { label: '302', value: 302 }]"
            float-label="Status"/>
        </div>
        <!-- Searchfield -->
        <div class="col-6 q-pa-md">
          <q-input color="primary"
          v-model="filters.comments"
          @input="applyFilters"
          :float-label="getString('comments')"/>
        </div>
      </div>
      <div class="row q-pa-md" style="justify-content: space-between;">
        <div class="q-pa-md" style="vertical-align: middle;">
          <q-btn
            :disable="!activeFilters"
            @click="resetFilters()"
            class="capitalize ctrl-button"
            :color="activeFilters ? 'primary' : 'grey-4'"
            label="Reset"
          />
        </div>
      </div>
    </div>
    </transition>
    <table-overview
      :endpoint="endpoint"
      :responseFormat="'redirects'"
      :noDataFormat="'redirects'"
      :columns="columns"
      :permission="permission"
      :module="'redirect'"
      :selectable="true">
    </table-overview>
  </div>
</template>

<script>
import EventBus from '@/event-bus';
import TableOverview from '@/components/overview/TableOverview.vue';
// Mixins
import filtersMixin from '@/mixins/tables/filtersMixin';
import getString from '@/helpers/GetStrings';

export default {
  mixins: [
    filtersMixin,
  ],
  components: {
    TableOverview,
  },
  data() {
    return {
      rowSelection: [],
      filters: {
        location_from: '',
        location_to: '',
        status: '',
        comments: '',
      },
      // Name of the module defined in user rights. eg. blog for blog.edit, blog.add etc..
      permission: 'redirect',
      // Where data is fetched from
      endpoint: 'redirect',
      // The object key from the response object
      responseData: {
        single: 'redirect',
        multiple: 'redirects',
      },
      // Used for navigation on the front-end (matching the router)
      module: 'redirect',
      searchTerm: '',
      selectable: true,
      isLoading: false,
      columns: [
        {
          name: '__slot:location_from',
          title: getString('module.redirect.fields.location_from'),
          sortField: 'location_from',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s text-ellipsis',
        },
        {
          name: '__slot:location_to',
          title: getString('module.redirect.fields.location_to'),
          sortField: 'location_to',
          titleClass: 'text-left sortable vcol-m',
          dataClass: 'text-left vcol-m text-ellipsis',
        },
        {
          name: 'datetime',
          title: getString('datetime.label'),
          sortField: 'show_once',
          titleClass: 'text-left sortable vcol-s',
          dataClass: 'text-left vcol-s',
          callback: 'tableFormatTime',
        },
        {
          name: 'status',
          title: 'Status',
          sortField: 'status',
          titleClass: 'text-left sortable vcol-xs',
          dataClass: 'text-left vcol-xs text-ellipsis',
        },
        {
          name: '__slot:catch_all',
          title: getString('module.redirect.fields.catch_all'),
          titleClass: 'text-center vcol-xs',
          dataClass: 'text-center vcol-xs',
        },
      ],
      hideFilters: true,
      preferences: {
        page: 1,
        perPage: 10,
        sortField: null,
        sortOrder: null,
      },
    };
  },
  methods: {
    getString,

    removeRows(key) {
      EventBus.$emit('row-selection:delete', key);
    },
  },

  mounted() {
    EventBus.$on('row-selection:update', (payload) => {
      this.rowSelection = payload;
    });
  },
  destroyed() {
    EventBus.$off('row-selection:update');
  },
};
</script>

<style lang="stylus" scoped>
@import '~variables';
</style>
